import { isEmpty } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { v4 } from 'uuid';

import { type ImportedScheduledRow, mapScheduleRowToFixing } from '@/App/tiles/shared/import/accuSchedule.ts';
import type { AccumulatorType } from '@/App/tiles/shared/import/common.ts';
import AddFixing from '@/App/tiles/TargetAccumulator/Schedule/ScheduleHeader/AddFixing.tsx';
import ExportSchedule from '@/App/tiles/TargetAccumulator/Schedule/ScheduleHeader/ExportSchedule.tsx';
import ImportSchedule from '@/App/tiles/TargetAccumulator/Schedule/ScheduleHeader/ImportSchedule.tsx';
import ResetScheduler from '@/App/tiles/TargetAccumulator/Schedule/ScheduleHeader/ResetScheduler.tsx';
import {
  useCheckTargetAccumulatorByTradeCapture,
  useTargetAccumulatorSelector,
} from '@/App/tiles/TargetAccumulator/TargetAccumulatorContext.tsx';
import { isDefined } from '@/components/sg-design-system/utils/index.ts';
import type { AccumulatorScheduleLine, TargetAccuType } from '@/store/targetAccumulator/state.ts';

type Props = {
  parametersAreVisible?: boolean;
  setParametersAreVisible?: (isVisible: boolean) => void;
};

const ScheduleHeader = ({ parametersAreVisible = true, setParametersAreVisible }: Props) => {
  const customFields = useTargetAccumulatorSelector((targetAccumulator) => targetAccumulator?.properties.customFields);
  const scheduleIds = useTargetAccumulatorSelector((targetAccumulator) => targetAccumulator?.properties.scheduleIds);
  const accuType = useTargetAccumulatorSelector((targetAccumulator) => targetAccumulator?.properties.accuType);
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const isModified = isDefined(customFields) && !isEmpty(customFields);
  const shouldShowConfirmation = isDefined(scheduleIds) && !isEmpty(scheduleIds);

  const toggleTableVisibility = useCallback(() => {
    sgwtWebAnalytics?.trackEvent(
      'Target Accumulator',
      parametersAreVisible ? 'Hide schedule parameters' : 'Show schedule parameters',
    );
    setParametersAreVisible?.(!parametersAreVisible);
  }, [parametersAreVisible, setParametersAreVisible, sgwtWebAnalytics]);

  const checkTargetAccumulatorWithTradeCapture = useCheckTargetAccumulatorByTradeCapture();

  const resetSchedule = () => {
    sgwtWebAnalytics?.trackEvent('Target Accumulator', 'Reset schedule');

    return checkTargetAccumulatorWithTradeCapture({
      schedule: null,
    });
  };

  const addLineSchedule = () => {
    const newId = v4();
    sgwtWebAnalytics?.trackEvent('Target Accumulator', 'Add line to schedule', newId);
    return checkTargetAccumulatorWithTradeCapture({
      schedule: { [newId]: {} as AccumulatorScheduleLine },
    });
  };

  const onImportSchedule = (rows: readonly ImportedScheduledRow[], accuType: AccumulatorType) => {
    const legsPatch = Object.fromEntries(rows.map((row) => [v4(), mapScheduleRowToFixing(row, accuType)]));
    sgwtWebAnalytics?.trackEvent('Target Accumulator', 'Import schedule');

    const oldSchedule =
      scheduleIds?.reduce(
        (acc, id) => {
          acc[id] = null;
          return acc;
        },
        {} as Record<string, null>,
      ) ?? {};

    return checkTargetAccumulatorWithTradeCapture({
      schedule: { ...legsPatch, ...oldSchedule },
    });
  };

  return (
    <ul className="nav w-100">
      <li className="nav-item cursor-pointer" onClick={toggleTableVisibility}>
        <span className="nav-link active ms-16px">
          <FormattedMessage id="accumulator.scheduler.header" />
        </span>
      </li>

      <li className="nav-item form-inline ms-auto">
        <div className="nav-link pe-2">
          <ResetScheduler isScheduleModified={isModified} resetSchedule={resetSchedule} />

          <ExportSchedule />

          <ImportSchedule
            accuType={accuType as TargetAccuType}
            onImportSchedule={onImportSchedule}
            shouldShowConfirmation={shouldShowConfirmation}
          />

          <AddFixing addLineSchedule={addLineSchedule} />

          {isDefined(setParametersAreVisible) && (
            <button type="button" className="btn btn-sm btn-link btn-icon-text pe-0" onClick={toggleTableVisibility}>
              <i className="icon icon-sm line-height-1 btn-group text-xxlarge ms-auto">
                {parametersAreVisible ? 'unfold_more' : 'unfold_less'}
              </i>
            </button>
          )}
        </div>
      </li>
    </ul>
  );
};

export default ScheduleHeader;
